/**
 * BundleBasket — the persistent multi-select side rail (Bundle Builder Phase 2).
 *
 * Renders the server-side basket: each item shows its numeric-prefixed bundle
 * label (editable inline), with move up/down, drag-and-drop reorder, and remove.
 * Footer actions: clear, save as named draft, download zip, export merged PDF.
 * All mutations are delegated to the parent (which owns the server sync); this
 * component is presentational plus local drag state and label-edit state.
 */
import { useState } from "react";
import {
  Layers, ChevronUp, ChevronDown, X, Trash2, Download,
  FileText, Save, Pencil,
} from "lucide-react";

export default function BundleBasket({
  items = [],
  busy = false,
  onMove,
  onRemove,
  onRelabel,
  onReorder,
  onClear,
  onSaveDraft,
  onDownloadZip,
  onExportPdf,
}) {
  const [dragId, setDragId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editValue, setEditValue] = useState("");

  const startEdit = (item) => {
    setEditId(item.item_id);
    // Strip the "NN — " prefix so the user edits just the label text.
    setEditValue((item.label || "").replace(/^\s*\d+\s*[—\-–]\s*/, ""));
  };
  const commitEdit = () => {
    if (editId && editValue.trim()) onRelabel(editId, editValue.trim());
    setEditId(null);
    setEditValue("");
  };

  const handleDrop = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const ids = items.map((i) => i.item_id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    onReorder(ids);
    setDragId(null);
  };

  return (
    <aside
      data-testid="bundle-basket"
      className="w-full rounded-lg border border-navy-border bg-navy-card p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Layers className="text-teal" size={18} />
          <h2 className="text-sm font-semibold text-white">Bundle</h2>
          <span
            data-testid="bundle-count"
            className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-teal/20 text-teal border border-teal"
          >
            {items.length}
          </span>
        </div>
        {items.length > 0 && (
          <button
            type="button"
            data-testid="bundle-clear"
            onClick={onClear}
            disabled={busy}
            className="text-xs text-slate-400 hover:text-red-300 inline-flex items-center gap-1"
          >
            <Trash2 size={13} /> Clear
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <p data-testid="bundle-empty" className="text-xs text-slate-500 py-6 text-center">
          Add documents from the results to start a bundle.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((item, idx) => (
            <li
              key={item.item_id}
              data-testid={`bundle-item-${item.item_id}`}
              draggable
              onDragStart={() => setDragId(item.item_id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(item.item_id)}
              className="flex items-center gap-1.5 rounded-md border border-navy-border bg-navy-hover px-2 py-1.5"
            >
              <div className="flex flex-col">
                <button
                  type="button"
                  data-testid={`bundle-up-${item.item_id}`}
                  onClick={() => onMove(item.item_id, "up")}
                  disabled={busy || idx === 0}
                  className="text-slate-400 hover:text-teal disabled:opacity-30"
                  aria-label="Move up"
                >
                  <ChevronUp size={13} />
                </button>
                <button
                  type="button"
                  data-testid={`bundle-down-${item.item_id}`}
                  onClick={() => onMove(item.item_id, "down")}
                  disabled={busy || idx === items.length - 1}
                  className="text-slate-400 hover:text-teal disabled:opacity-30"
                  aria-label="Move down"
                >
                  <ChevronDown size={13} />
                </button>
              </div>

              {editId === item.item_id ? (
                <input
                  data-testid={`bundle-label-input-${item.item_id}`}
                  value={editValue}
                  autoFocus
                  onChange={(e) => setEditValue(e.target.value)}
                  onBlur={commitEdit}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitEdit();
                    if (e.key === "Escape") { setEditId(null); setEditValue(""); }
                  }}
                  className="flex-1 min-w-0 bg-navy-card border border-teal rounded px-1.5 py-0.5 text-xs text-white"
                />
              ) : (
                <button
                  type="button"
                  data-testid={`bundle-label-${item.item_id}`}
                  onClick={() => startEdit(item)}
                  title="Click to rename (bundle only)"
                  className="flex-1 min-w-0 text-left text-xs text-white truncate hover:text-teal inline-flex items-center gap-1"
                >
                  <span className="truncate">{item.label}</span>
                  <Pencil size={11} className="shrink-0 opacity-40" />
                </button>
              )}

              <button
                type="button"
                data-testid={`bundle-remove-${item.item_id}`}
                onClick={() => onRemove(item.item_id)}
                disabled={busy}
                className="text-slate-400 hover:text-red-300 shrink-0"
                aria-label="Remove"
              >
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {items.length > 0 && (
        <div className="mt-4 space-y-2">
          <button
            type="button"
            data-testid="bundle-export-pdf"
            onClick={onExportPdf}
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold bg-teal hover:bg-teal/90 text-navy disabled:opacity-50"
          >
            <FileText size={14} /> Export bundle PDF
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="bundle-download-zip"
              onClick={onDownloadZip}
              disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs bg-slate-800 text-slate-200 border border-navy-border hover:bg-slate-700 disabled:opacity-50"
            >
              <Download size={13} /> Zip
            </button>
            <button
              type="button"
              data-testid="bundle-save-draft"
              onClick={onSaveDraft}
              disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs bg-slate-800 text-slate-200 border border-navy-border hover:bg-slate-700 disabled:opacity-50"
            >
              <Save size={13} /> Save draft
            </button>
          </div>
        </div>
      )}
    </aside>
  );
}
